import React from "react";

export const emptyAccountForm = {
    name: "",
    domain: "",
    is_active: true,
};

export const emptyUserForm = {
    email: "",
    username: "",
    phone_number: "",
    role: "user",
    email_verified: false,
    is_active: true,
};

export const emptySubscriptionForm = {
    subscription_type: "company",
    user_id: "",
    account_id: "",
    expires_at: "",
    is_active: true,
};

export const PENDING_WINDOW_GLASS_PAGE_SIZE = 100;

export const inputClass =
    "h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-200";
export const checkboxClass =
    "h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-300";
export const primaryButtonClass =
    "inline-flex h-10 items-center justify-center rounded-xl bg-slate-900 px-4 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60";
export const secondaryButtonClass =
    "inline-flex h-10 items-center justify-center rounded-xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50";
export const saveButtonClass =
    "inline-flex h-10 items-center justify-center rounded-lg bg-slate-900 px-3 text-sm font-medium text-white transition hover:bg-slate-800";
export const mutedButtonClass =
    "inline-flex h-10 items-center justify-center rounded-lg border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50";
export const dangerButtonClass =
    "inline-flex h-10 items-center justify-center rounded-lg bg-rose-600 px-3 text-sm font-medium text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60";

export const toBool = (value) => Boolean(value);

export const toLocalInputValue = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return typeof value === "string" ? value.slice(0, 16) : "";
    }

    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
        date.getHours()
    )}:${pad(date.getMinutes())}`;
};

export const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
};

export const isSubscriptionCurrentlyActive = (sub) => {
    if (!sub?.is_active || !sub?.expires_at) return false;
    const expiry = new Date(sub.expires_at);
    if (Number.isNaN(expiry.getTime())) return false;
    return expiry > new Date();
};

export const buildUserDrafts = (rows) =>
    Object.fromEntries(
        rows.map((user) => [
            user.user_id,
            {
                email: user.email || "",
                username: user.username || "",
                phone_number: user.phone_number || "",
                role: user.role || "user",
                is_active: toBool(user.is_active),
                email_verified: toBool(user.email_verified),
            },
        ])
    );

export const buildAccountDrafts = (rows) =>
    Object.fromEntries(
        rows.map((account) => [
            account.account_id,
            {
                name: account.name || "",
                is_active: toBool(account.is_active),
            },
        ])
    );

export const buildDomainDrafts = (rows) => {
    const entries = [];
    rows.forEach((account) => {
        (account.domains || []).forEach((domain) => {
            entries.push([
                domain.account_domain_id,
                {
                    domain: domain.domain || "",
                    is_active: toBool(domain.is_active),
                },
            ]);
        });
    });
    return Object.fromEntries(entries);
};

export const buildSubscriptionDrafts = (rows) =>
    Object.fromEntries(
        rows.map((sub) => [
            sub.subscription_id,
            {
                subscription_type: sub.subscription_type || "company",
                user_id: sub.user_id ? String(sub.user_id) : "",
                account_id: sub.account_id ? String(sub.account_id) : "",
                expires_at: toLocalInputValue(sub.expires_at),
                is_active: toBool(sub.is_active),
            },
        ])
    );

export const StatusPill = ({ active, activeLabel = "Active", inactiveLabel = "Inactive" }) => (
    <span
        className={`inline-flex h-7 w-7 items-center justify-center rounded-full border ${active
            ? "border-emerald-200 bg-emerald-100"
            : "border-rose-200 bg-rose-100"
            }`}
        title={active ? activeLabel : inactiveLabel}
        aria-label={active ? activeLabel : inactiveLabel}
    >
        <span className={`h-2.5 w-2.5 rounded-full ${active ? "bg-emerald-600" : "bg-rose-600"}`} />
    </span>
);

export const StatCard = ({ label, value, helper }) => (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="text-sm text-slate-500">{label}</div>
        <div className="mt-2 text-3xl font-extrabold tracking-tight text-slate-900">{value}</div>
        {helper ? <div className="mt-1 text-xs text-slate-500">{helper}</div> : null}
    </div>
);

export const SectionCard = ({ title, description, children, right }) => (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex flex-col gap-3 border-b border-slate-200 pb-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
                <h2 className="text-lg font-semibold text-slate-900 text-left">{title}</h2>
                {description ? <p className="mt-1 text-sm text-slate-500 text-left">{description}</p> : null}
            </div>
            {right}
        </div>
        <div className="pt-5">{children}</div>
    </section>
);

export const PaginationBar = ({
    page,
    totalPages,
    totalCount,
    totalLabel,
    onPrevious,
    onNext,
    disabled = false,
}) => (
    <div className="mt-4 flex items-center justify-between gap-3 text-sm text-slate-600">
        <div>
            Page {page} of {totalPages} | {totalCount} {totalLabel}
        </div>
        <div className="flex gap-2">
            <button className={mutedButtonClass} onClick={onPrevious} disabled={disabled || page <= 1}>
                Prev
            </button>
            <button
                className={mutedButtonClass}
                onClick={onNext}
                disabled={disabled || page >= totalPages}
            >
                Next
            </button>
        </div>
    </div>
);
